import * as React from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Table from "@mui/joy/Table";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import Link from "@mui/joy/Link";
import Stack from "@mui/joy/Stack";
import { formatDateTime } from "../../common/dateTime";
import { NotificationService } from "../../common/notification";
import { BidHistoryEvent, BidHistoryReport } from "../../models/bid";
import { OrderFailReasonDescription, OrderStatusDescription } from "../../models/order";
import { bidsHistoryApi } from "./bidsApi";

type Props = {
    bidId: number | null;
    onClose: () => void;
};

const failReasonLabels = OrderFailReasonDescription as Record<number, string>;
const orderStatusLabels = OrderStatusDescription as Record<number, string>;

function resultChip(event: BidHistoryEvent) {
    if (event.success === true) {
        return <Chip size="sm" color="success" variant="soft">Успех</Chip>;
    }
    if (event.success === false) {
        return <Chip size="sm" color="danger" variant="soft">Неуспех</Chip>;
    }
    if (event.orderStatus != null) {
        return <Chip size="sm" color="warning" variant="soft">{event.detail || "В процессе"}</Chip>;
    }
    return <Typography level="body-xs">—</Typography>;
}

function eventDetails(event: BidHistoryEvent): React.ReactNode {
    const parts: React.ReactNode[] = [];
    if (event.failReason != null) {
        parts.push(
            <Typography key="fail" level="body-xs" color="danger">
                {failReasonLabels[event.failReason] ?? `Причина #${event.failReason}`}
            </Typography>
        );
    }
    if (event.orderStatus != null) {
        parts.push(
            <Typography key="status" level="body-xs">
                Статус: {orderStatusLabels[event.orderStatus] ?? event.orderStatus}
            </Typography>
        );
    }
    if (event.completedAt) {
        parts.push(
            <Typography key="done" level="body-xs">
                Завершено: {formatDateTime(event.completedAt)}
            </Typography>
        );
    }
    if (event.comment) {
        parts.push(
            <Typography key="comment" level="body-xs">
                {event.comment}
            </Typography>
        );
    }
    if (event.detail && event.success == null && event.orderStatus == null) {
        parts.push(
            <Typography key="detail" level="body-xs">
                {event.detail}
            </Typography>
        );
    }
    if (parts.length === 0) {
        return <Typography level="body-xs">—</Typography>;
    }
    return <Stack spacing={0.25}>{parts}</Stack>;
}

export function BidHistoryModal({ bidId, onClose }: Props) {
    const [loading, setLoading] = React.useState(false);
    const [report, setReport] = React.useState<BidHistoryReport | null>(null);

    React.useEffect(() => {
        if (bidId == null) {
            setReport(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setReport(null);
        bidsHistoryApi(bidId)
            .then((result) => {
                if (cancelled) {
                    return;
                }
                if (result.result && result.value) {
                    setReport(result.value);
                } else {
                    NotificationService.alertError("Не удалось загрузить историю заявки");
                    onClose();
                }
            })
            .catch((err) => {
                console.error(err);
                if (!cancelled) {
                    NotificationService.alertError("Не удалось загрузить историю заявки");
                    onClose();
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [bidId, onClose]);

    return (
        <Modal open={bidId != null} onClose={onClose}>
            <ModalDialog
                sx={{
                    width: { xs: "100%", sm: 960 },
                    maxWidth: "100%",
                    maxHeight: "90vh",
                    overflow: "auto",
                }}
            >
                <ModalClose />
                <Typography level="h4">
                    История заявки{bidId != null ? ` #${bidId}` : ""}
                </Typography>
                {report?.hashName && (
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                        {report.hashName} · {report.bought}/{report.count} куплено
                        {report.reserved > 0 ? ` · ${report.reserved} в резерве` : ""}
                    </Typography>
                )}
                {loading ? (
                    <CircularProgress />
                ) : report ? (
                    <Sheet sx={{ overflow: "auto" }}>
                        <Table stickyHeader size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: 160 }}>Время</th>
                                    <th style={{ width: 180 }}>Событие</th>
                                    <th style={{ width: 160 }}>Продавец</th>
                                    <th style={{ width: 110 }}>Результат</th>
                                    <th>Подробности</th>
                                    <th style={{ width: 80 }}>Ордер</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.events.map((event, index) => (
                                    <tr key={`${event.type}-${event.at}-${event.orderId ?? index}`}>
                                        <td>
                                            <Typography level="body-xs">{formatDateTime(event.at)}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{event.title}</Typography>
                                        </td>
                                        <td>
                                            {event.sellerId != null ? (
                                                <Stack spacing={0.25}>
                                                    <Link
                                                        href={`/app/users/${event.sellerId.toString()}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        level="body-xs"
                                                    >
                                                        {event.sellerId.toString()}
                                                    </Link>
                                                    {event.sellerName && (
                                                        <Typography level="body-xs">{event.sellerName}</Typography>
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography level="body-xs">—</Typography>
                                            )}
                                        </td>
                                        <td>{resultChip(event)}</td>
                                        <td>{eventDetails(event)}</td>
                                        <td>
                                            {event.orderId != null ? (
                                                <Link
                                                    href={`/app/orders/${event.orderId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    #{event.orderId}
                                                </Link>
                                            ) : (
                                                <Typography level="body-xs">—</Typography>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                ) : null}
            </ModalDialog>
        </Modal>
    );
}
